import { PropsWithChildren } from 'react';

import translations from '@webclient/lib/agent/endpoints/translations';
import getCurrentLanguageFromLocalStorage from '@webclient/lib/getCurrentLanguageFromLocalStorage';

import { LanguageType } from '@webclient/shared-types/translation';

// eslint-disable-next-line import-alias/import-alias
import definedKeys from '../translations/en.json';

import * as Sentry from '@sentry/react';
import {
  DevTools,
  FormatSimple,
  LanguageDetector,
  Tolgee,
  TolgeeProvider,
} from '@tolgee/react';

const DEFAULT_LANGUAGE = 'en';

export const AVAILABLE_LANGUAGES: Array<LanguageType> = [
  DEFAULT_LANGUAGE,
  'es',
  'de',
  'fr',
  'id',
  'pt',
] as const;

const KNOWN_MISSING_KEYS = new Set<string>();
const ORIGINAL_KEYS = flattenKeys(definedKeys);

function flattenKeys(obj: Record<string, any>, parentKey = ''): string[] {
  return Object.keys(obj).flatMap((key) => {
    const newKey = parentKey ? `${parentKey}.${key}` : key;
    return typeof obj[key] === 'object'
      ? flattenKeys(obj[key], newKey)
      : newKey;
  });
}

const tolgee = Tolgee()
  .use(DevTools())
  .use(FormatSimple())
  .use(LanguageDetector())
  .init({
    availableLanguages: [...AVAILABLE_LANGUAGES],
    defaultLanguage: getCurrentLanguageFromLocalStorage(DEFAULT_LANGUAGE),
    fallbackLanguage: DEFAULT_LANGUAGE,
    ...(String(import.meta.env.VITE_KNOWZ_WEBSITE_URL).includes('localhost')
      ? {
          apiUrl: import.meta.env.VITE_APP_TOLGEE_API_URL,
          apiKey: import.meta.env.VITE_APP_TOLGEE_API_KEY,
          projectId: import.meta.env.VITE_APP_TOLGEE_PROJECT_ID,
        }
      : {
          staticData: {
            en: async () => (await translations.getOne('en')).data,
            es: async () => (await translations.getOne('es')).data,
            de: async () => (await translations.getOne('de')).data,
            fr: async () => (await translations.getOne('fr')).data,
            id: async () => (await translations.getOne('id')).data,
            pt: async () => (await translations.getOne('pt')).data,
          },
        }),
    onTranslationMissing: (info) => {
      if (!KNOWN_MISSING_KEYS.has(info.key)) {
        if (import.meta.env.MODE === 'production') {
          Sentry.captureException(
            new Error(`Translation missing for ${info.key}`),
          );

          console.error(`Translation missing for ${info.key}`);
        } else {
          if (ORIGINAL_KEYS.includes(info.key)) {
            KNOWN_MISSING_KEYS.size ||
              console.warn(
                'Some translations are not pushed to Tolgee. Please run `tolgee push`.',
              );
          } else {
            console.error(`Translation missing for ${info.key}`);
          }
        }

        KNOWN_MISSING_KEYS.add(info.key);
      }
      return 'Translation missing';
    },
  });

export default function TranslationProvider({ children }: PropsWithChildren) {
  return <TolgeeProvider tolgee={tolgee}>{children}</TolgeeProvider>;
}
